import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, MapPin, ArrowRight, Check, Bell } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useNotify } from "@/context/NotifyContext";
import { restaurantService } from "@/lib/services";
import { getQRSource, getUTM } from "@/lib/utm";
import { track } from "@/lib/analytics";
import { logger } from "@/lib/logger";

export default function RestaurantOrigin() {
  const { t } = useLanguage();
  const { openNotify } = useNotify();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Auto-detect via URL param
  useEffect(() => {
    const qr = getQRSource();
    track("restaurant_origin_section_viewed");
    if (qr.restaurant_id) {
      restaurantService
        .getRestaurantById(qr.restaurant_id)
        .then((r) => {
          if (r) {
            setSelected(r);
            track("qr_source_detected", { restaurant_id: qr.restaurant_id, qr_source: qr.qr_source, source: qr.source });
          }
        })
        .catch((err) => {
          logger.warn("[origin] auto-detect restaurant failed:", err);
        });
    }
  }, []);

  // Live search debounced
  useEffect(() => {
    if (!query || query.length < 1) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const id = setTimeout(async () => {
      try {
        const res = await restaurantService.searchRestaurants(query);
        if (!cancelled) setResults(res.items || []);
      } catch (err) {
        logger.warn("[origin] search failed:", err);
        if (!cancelled) setResults([]);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    function onClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSelect = async (r) => {
    setSelected(r);
    setOpen(false);
    setQuery("");
    const utm = getUTM();
    const qr = getQRSource();
    try {
      await restaurantService.trackRestaurantOriginSelection({
        restaurant_id: r.id,
        restaurant_name: r.name,
        city: r.city,
        qr_source: qr.qr_source,
        source: qr.source,
        ...utm,
      });
      track("restaurant_selected", { restaurant_id: r.id, restaurant_name: r.name, city: r.city });
    } catch (err) {
      logger.warn("[origin] track selection failed:", err);
    }
  };

  return (
    <div className="card-premium" data-testid="restaurant-origin-card">
      {!selected ? (
        <div ref={containerRef} className="relative">
          <label htmlFor="restaurant-search" className="form-label">
            {t("home.origin.searchLabel")}
          </label>
          <div className="search-availability-notice" data-testid="search-availability-notice">
            <span className="eyebrow">{t("home.origin.availabilityEyebrow")}</span>
            <span className="body-text">{t("home.origin.availabilityBody")}</span>
          </div>
          <div className="relative">
            <Search
              size={18}
              strokeWidth={1.75}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
            />
            <input
              id="restaurant-search"
              ref={inputRef}
              type="text"
              autoComplete="off"
              data-testid="restaurant-search-input"
              className="field-input with-icon"
              placeholder={t("home.origin.searchPlaceholder")}
              value={query}
              onChange={(ev) => {
                setQuery(ev.target.value);
                setOpen(true);
                if (ev.target.value.length === 1) track("restaurant_search_started");
              }}
              onFocus={() => setOpen(true)}
            />
          </div>

          {open && query && (
            <div className="absolute z-30 left-0 right-0 mt-2 bg-surface border border-soft rounded-md shadow-lg overflow-hidden" data-testid="restaurant-search-results">
              {results.length === 0 ? (
                <div className="px-4 py-4 body-sm">{t("home.origin.noResults")}</div>
              ) : (
                results.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => handleSelect(r)}
                    data-testid={`restaurant-result-${r.id}`}
                    className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-olive-faint transition-colors"
                  >
                    <div>
                      <div className="text-primary font-medium text-[15px]">{r.name}</div>
                      <div className="text-muted text-[13px] flex items-center gap-1 mt-0.5">
                        <MapPin size={12} /> {r.city} · {r.cuisine}
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-muted" />
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-5" data-testid="restaurant-selected-card">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 border border-soft">
              <img src={selected.image_url} alt={selected.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Check size={14} className="text-accent" />
                <span className="text-[11px] uppercase tracking-widest text-accent">{t("home.origin.detected")}</span>
              </div>
              <div className="font-display text-[28px] text-primary leading-tight mt-1">{selected.name}</div>
              <div className="text-muted text-[14px] mt-1 flex items-center gap-1"><MapPin size={12} /> {selected.city} · {selected.cuisine}</div>
            </div>
          </div>

          {selected.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selected.tags.slice(0, 6).map((tg) => (
                <span key={tg} className="tag-chip active text-[12px] py-1.5">{tg}</span>
              ))}
            </div>
          )}

          <div className="notify-origin-notice" data-testid="origin-notify-notice">
            <span className="eyebrow">{t("notify.origin.eyebrow")}</span>
            <p className="body-md" style={{ color: "var(--color-primary)" }}>
              {t("notify.sub")}
            </p>
            <button
              type="button"
              onClick={() => openNotify("origin-selected")}
              data-testid="origin-notify-cta"
              className="btn-primary"
            >
              <Bell size={16} strokeWidth={1.75} />
              {t("notify.origin.cta")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
